import moment from 'moment-timezone';
import {
	BookingBuffer,
	DeliveryOption,
	DeliveryOptionWithProducts,
	DeliveryPeriod,
	DeliveryTime,
	Languages,
	OpeningHoursDayType,
	ProductApi,
	DeliveryType,
	DeliveryTypes,
	DeliveryOptionHandlingTimes,
	DeliveryTimeSlotTimes,
	DeliveryOpeningHoursBehaviour,
} from 'common/types';
import { FieldValues } from 'common/frontend/firebase/firestore';

export interface DeliveryOptionForm {
	type: DeliveryType;
	name: string;
	description: string;
	deliveryPrice: number;
	pickupPrice: number;
	vatPercent: number;
	minimalDurationInSeconds: number;
	locationIds: string[];
	products: ProductApi[];
	bookingBuffer: BookingBuffer;
	deliveryPeriods: DeliveryPeriod[];
	beforeRentalMinutes: number;
	afterRentalMinutes: number;
	hidden: boolean;
	returnInstructions: string | undefined;
	handlingTimes: DeliveryOptionHandlingTimes;
	openingHoursBehaviour: DeliveryOpeningHoursBehaviour;
}

const getDefaultDeliveryTimeslots = (): DeliveryTimeSlotTimes[] => [
	{ startTime: '09:00', endTime: '10:00' },
	{ startTime: '17:00', endTime: '18:00' },
];

export const getDefaultDeliveryTimeSlot = (
	addedTimeSlots?: DeliveryTimeSlotTimes[],
): DeliveryTimeSlotTimes => {
	if (!addedTimeSlots?.length) {
		return { startTime: '09:00', endTime: '10:00' };
	}
	const lastSlot = addedTimeSlots[addedTimeSlots.length - 1];
	const newStartTime =
		lastSlot.endTime !== '23:59'
			? moment(lastSlot.endTime, 'HH:mm').add(15, 'minutes').format('HH:mm')
			: '00:00';
	const newEndTime = moment(newStartTime, 'HH:mm').add(1, 'hour').format('HH:mm');
	return { startTime: newStartTime, endTime: newEndTime };
};

export const getDefaultDeliveryTime = (dayType: OpeningHoursDayType = 'allDays'): DeliveryTime => {
	return { dayType, slots: getDefaultDeliveryTimeslots() };
};

export const getDefaultDeliveryPeriod = (startDate?: string | null): DeliveryPeriod => {
	if (startDate && moment().startOf('day').isSameOrBefore(moment(startDate, 'YYYY-MM-DD'))) {
		return {
			startDate: moment(startDate, 'YYYY-MM-DD').add(1, 'days').format('YYYY-MM-DD'),
			endDate: null,
			times: [getDefaultDeliveryTime()],
		};
	}
	return {
		startDate: moment().format('YYYY-MM-DD'),
		endDate: null,
		times: [getDefaultDeliveryTime()],
	};
};

export const getDefaultBookingBuffer = (): BookingBuffer => ({
	daysBefore: 0,
	minutesBefore: 0,
	latestTime: 6,
});

export const getDataToSubmit = (
	form: DeliveryOptionForm,
	shopId: string,
	maxDeliveryOptionOrderIndex: number,
	defaultLanguage?: Languages,
	oldValues?: DeliveryOptionWithProducts,
): [Omit<DeliveryOption, 'id'>, ProductApi[]] => {
	const {
		name,
		description,
		deliveryPrice,
		pickupPrice,
		beforeRentalMinutes,
		afterRentalMinutes,
		products,
		deliveryPeriods,
		returnInstructions,
		type,
		...others
	} = form;

	const hasPickupPrice = deliveryTypeHasPickupPrice(type);
	const returnInstructionsChanged = oldValues?.returnInstructions?.def !== returnInstructions;

	const deliveryOption: Omit<DeliveryOption, 'id'> = {
		...others,
		type,
		shopId,
		createdAt: oldValues?.createdAt ?? new Date().toISOString(),
		name: {
			...oldValues?.name,
			def: name,
			...(defaultLanguage && { [defaultLanguage]: name }),
		},
		description: {
			...oldValues?.description,
			def: description,
			...(defaultLanguage && { [defaultLanguage]: description }),
		},
		deliveryPeriods: (deliveryPeriods ?? []).map((period) => ({
			...period,
			endDate: period.endDate ?? null,
		})),
		price: {
			delivery: deliveryPrice,
			pickup: hasPickupPrice ? pickupPrice ?? 0 : 0,
		},
		...(returnInstructionsChanged && {
			returnInstructions:
				returnInstructions !== undefined
					? {
							...oldValues?.returnInstructions,
							def: returnInstructions,
							...(defaultLanguage && { [defaultLanguage]: returnInstructions }),
					  }
					: (FieldValues.delete() as any),
		}),
		orderIndex: maxDeliveryOptionOrderIndex + 1,
	};
	return [deliveryOption, products];
};

export const isDeliveryTimeSlotDuplicate = (
	slot: DeliveryTimeSlotTimes,
	index: number,
	slots: DeliveryTimeSlotTimes[],
) => {
	return slots.some(
		(s, i) => s.startTime === slot.startTime && s.endTime === slot.endTime && i !== index,
	);
};

export const deliveryTimesHaveDuplicateSlots = (times: DeliveryTime[]) => {
	return times.some((time) =>
		time.slots.some((slot, index, slots) => isDeliveryTimeSlotDuplicate(slot, index, slots)),
	);
};

export const deliveryTypeHasPickupPrice = (type: DeliveryType) => {
	return type === DeliveryTypes.DELIVERY_AND_OPTIONAL_PICKUP;
};
